import React from 'react';
import cn from 'classnames';
import { LinkWithQuery } from '../LinkWithQuery';
import {
  ROOT_ROUTE,
  STUDIO_ROUTE,
  FIGMA_TO_STORYCARDS_ROUTE,
  ANALYTICS_ROUTE,
  SEGMENTS_ROUTE,
  FLOW_ROUTE,
  DATA_AND_PRIVACY_ROUTE,
  SUBSCRIPTION_PLANS_PAGE,
  CARDS_FORMAT_ROUTE,
  COLLABORATION_ROUTE,
  ABOUT_ROUTE,
  PRESS_CENTER_ROUTE,
  PRIVACY_POLICY_ROUTE,
  DPA_ROUTE,
  INSPIRATION_LINK,
  INFOGRAPHICS_LINK,
  AI_ROUTE,
  ENGAGEMENT_AI_ROUTE,
  BRAND_GOVERNANCE_ROUTE,
  INTEGRATIONS_ROUTE,
  HOSTING_ROUTE,
  SECURITY_ROUTE,
  API_ROUTE,
  ENTERPRISE_CONTACT_LINK,
  INSURANCE_SOLUTIONS_ROUTE,
  BANKING_SOLUTIONS_ROUTE,
  PAYMENTS_SOLUTIONS_ROUTE,
  HEALTHCARE_SOLUTIONS_ROUTE,
  REAL_ESTATE_SOLUTIONS_ROUTE,
  RETAIL_SOLUTIONS_ROUTE,
  TELECOMMUNICATIONS_SOLUTIONS_ROUTE,
  AGENCIES_ROUTE,
  PUBLISHERS_ROUTE,
  COMPARE_ROUTE,
} from 'components/constants';
import icons from 'assets/icons';
import translator from 'utils/translator';

import styles from './styles.module.scss';

interface FooterLinkType {
  label: string;
  url: string;
  newTab?: boolean;
}

interface FooterGroupType {
  title: string;
  titleId: string;
  links: FooterLinkType[];
}

interface Props {
  isPricingPage?: boolean;
  showCta?: boolean;
}

const _t = (path: string, pattern: object = {}) => translator.text(`${path}`, pattern);

const FooterLink = ({ link }: { link: FooterLinkType }) => {
  if (link.newTab) {
    return (
      <a
        className={cn(styles.link, { [styles.externalLink]: link.newTab })}
        href={link.url}
        target="_blank"
        rel="noreferrer"
      >
        <span>
          {link.label}
          {link.newTab && (
            <span className={styles.externalIndicator} aria-hidden="true">
              ↗
            </span>
          )}
        </span>
      </a>
    );
  }

  return (
    <LinkWithQuery className={styles.link} to={link.url}>
      {link.label}
    </LinkWithQuery>
  );
};

function Footer(props: Props) {
  const titleContent = 'Create anything. Operate at scale.';
  const textContent =
    'One enterprise platform to build, manage and continuously improve on-brand interactive experiences.';

  return (
    <footer id="footer" className={styles.footer}>
      {props.showCta && (
        <section className={styles.cta} aria-labelledby="footer-cta-title">
          <div>
            <h2 id="footer-cta-title" className={styles.title}>
              {titleContent}
            </h2>
          </div>

          <div className={styles.ctaContent}>
            <p className={styles.text}>{textContent}</p>
            {!props.isPricingPage && (
              <div className={styles.ctaActions}>
                <a href={ENTERPRISE_CONTACT_LINK} className={styles.ctaButtonSecondary}>
                  Talk to our enterprise team
                </a>
                <a href={SUBSCRIPTION_PLANS_PAGE} target="_blank" rel="noreferrer" className={styles.ctaButton}>
                  Start Creating
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      <div className={styles.footerMain}>
        <div className={styles.brandRow}>
          <LinkWithQuery to={ROOT_ROUTE} className={styles.logo}>
            <img src={icons.storycardsLogo} alt="Storycards" />
          </LinkWithQuery>
          <p className={styles.brandDescription}>
            Create, govern and improve interactive products in one enterprise platform.
          </p>
        </div>

        <nav className={styles.navigation} aria-label="Footer navigation">
          {footerGroups.map((group) => (
            <section className={styles.navigationGroup} key={group.title} aria-labelledby={`footer-${group.titleId}`}>
              <h2 id={`footer-${group.titleId}`}>{group.title}</h2>
              <ul>
                {group.links.map((link) => (
                  <li key={`${group.title}-${link.label}`}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
      </div>

      <div className={styles.footerBottom}>
        <p className={styles.copyright}>© {new Date().getFullYear()} Storycards, Inc. All rights reserved.</p>

        <nav className={styles.legalLinks} aria-label="Legal">
          {legalLinks.map((link) => (
            <FooterLink key={link.label} link={link} />
          ))}
        </nav>

        <div className={styles.socials} aria-label="Follow Storycards">
          {socials.map((item) => (
            <a
              href={item.url}
              key={item.url}
              className={cn(styles.socialLink, styles[item.name])}
              target="_blank"
              rel="noreferrer"
              aria-label={item.label}
            >
              <img src={item.image} alt="" />
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default Footer;

Footer.defaultProps = {
  isPricingPage: false,
  showCta: true,
};

const footerGroups: FooterGroupType[] = [
  {
    title: 'Platform',
    titleId: 'platform',
    links: [
      { label: _t('menu.aiCreation.title'), url: AI_ROUTE },
      { label: _t('menu.aiEngine.title'), url: ENGAGEMENT_AI_ROUTE },
      { label: _t('menu.editor.title'), url: STUDIO_ROUTE },
      { label: 'Figma to Storycards', url: FIGMA_TO_STORYCARDS_ROUTE },
      { label: _t('menu.flow.title'), url: FLOW_ROUTE },
      { label: _t('menu.cardsFormat.title'), url: CARDS_FORMAT_ROUTE },
      { label: _t('menu.infographics.title'), url: INFOGRAPHICS_LINK, newTab: true },
    ],
  },
  {
    title: 'Govern & scale',
    titleId: 'govern',
    links: [
      { label: _t('menu.brandGovernance.title'), url: BRAND_GOVERNANCE_ROUTE },
      { label: _t('menu.collaboration.title'), url: COLLABORATION_ROUTE },
      { label: _t('menu.hosting.title'), url: HOSTING_ROUTE },
      { label: _t('menu.security.title'), url: SECURITY_ROUTE },
    ],
  },
  {
    title: 'Data & optimization',
    titleId: 'data',
    links: [
      { label: _t('menu.analytics.title'), url: ANALYTICS_ROUTE },
      { label: _t('menu.dataCollection.title'), url: DATA_AND_PRIVACY_ROUTE },
      { label: _t('menu.segments.title'), url: SEGMENTS_ROUTE },
      { label: _t('menu.integrations.title'), url: INTEGRATIONS_ROUTE },
      { label: _t('menu.api.title'), url: API_ROUTE },
    ],
  },
  {
    title: 'Solutions',
    titleId: 'solutions',
    links: [
      { label: _t('menu.insurance.title'), url: INSURANCE_SOLUTIONS_ROUTE },
      { label: _t('menu.banking.title'), url: BANKING_SOLUTIONS_ROUTE },
      { label: _t('menu.payments.title'), url: PAYMENTS_SOLUTIONS_ROUTE },
      { label: _t('menu.healthcare.title'), url: HEALTHCARE_SOLUTIONS_ROUTE },
      { label: _t('menu.realEstate.title'), url: REAL_ESTATE_SOLUTIONS_ROUTE },
      { label: _t('menu.retail.title'), url: RETAIL_SOLUTIONS_ROUTE },
      { label: _t('menu.telecommunications.title'), url: TELECOMMUNICATIONS_SOLUTIONS_ROUTE },
      { label: 'Advertising agencies', url: AGENCIES_ROUTE },
      { label: 'Publishers & media', url: PUBLISHERS_ROUTE },
    ],
  },
  {
    title: 'Company',
    titleId: 'company',
    links: [
      { label: 'Compare platforms', url: COMPARE_ROUTE },
      { label: _t('menu.about'), url: ABOUT_ROUTE },
      { label: _t('menu.pressCenter'), url: PRESS_CENTER_ROUTE },
      { label: _t('menu.inspiration'), url: INSPIRATION_LINK, newTab: true },
    ],
  },
];

const legalLinks: FooterLinkType[] = [
  { label: _t('menu.privacy'), url: PRIVACY_POLICY_ROUTE },
  { label: _t('menu.tos'), url: '/terms.pdf?d=08022022', newTab: true },
  { label: _t('menu.dpa'), url: DPA_ROUTE },
];

const socials = [
  {
    name: 'facebook',
    label: 'Storycards on Facebook',
    image: icons.facebookIcon,
    url: 'https://www.facebook.com/my.storycards',
  },
  {
    name: 'instagram',
    label: 'Storycards on Instagram',
    image: icons.instagramIcon,
    url: 'https://www.instagram.com/_storycards',
  },
  {
    name: 'youtube',
    label: 'Storycards on YouTube',
    image: icons.youtubeIcon,
    url: 'https://www.youtube.com/channel/UC_tzryUqiQv2UjAyhFR-vOw',
  },
  {
    name: 'twitter',
    label: 'Storycards on X',
    image: icons.twitterIcon,
    url: 'https://twitter.com/_Storycards',
  },
  {
    name: 'linkedin',
    label: 'Storycards on LinkedIn',
    image: icons.linkedinIcon,
    url: 'https://www.linkedin.com/company/storycards-app',
  },
];
